import {Store} from '@effector/effector'
import {bind} from './render/bind'

export function groupBy<T extends {[field: string]: any}, K extends keyof T>(
  list: Store<T[]>,
  key: K
): Store<Array<{id: T[K]; items: T[]}>> {
  const getID = (item: T): T[K] => item[key]
  return list.map(items => {
    const itemsIds = items.map(getID)
    const ids = [...new Set(itemsIds)]
    const result: Array<{id: T[K]; items: T[]}> = ids.map(id => ({
      id,
      items: []
    }))
    for (let i = 0; i < items.length; i++) {
      const store = items[i]
      const storeId = itemsIds[i]
      const group = result[ids.indexOf(storeId)]
      group.items.push(store)
    }
    return result
  })
}

export function remap<T extends {[field: string]: any}, S extends keyof T>(
  store: Store<T>,
  key: S
): Store<T[S]>
export function remap<
  T extends {[field: string]: any},
  S extends {[field: number]: keyof T} | {[field: string]: keyof T}
>(
  store: Store<T>,
  shape: S
): {[K in keyof S]: S[K] extends keyof T ? Store<T[S[K]]> : never}

export function remap(
  store: Store<any>,
  shape: string | Array<any> | {[field: string]: any}
) {
  if (Array.isArray(shape)) {
    const result = [] as any
    for (let i = 0; i < shape.length; i++) {
      result[i] = store.map(bind(readField, shape[i]))
    }
    return result
  }
  if (typeof shape === 'object' && shape !== null) {
    const result = {} as any
    for (const key in shape) {
      result[key] = store.map(bind(readField as any, shape[key]))
    }
    return result
  }
  return store.map(bind(readField as any, shape))
}

export function storeField<T extends {[field: string]: any}, K extends keyof T>(
  store: Store<T>,
  key: K
): Store<T[K]> {
  return store.map(bind(readField, key))
}

function readField<T extends {[field: string]: any}, K extends keyof T>(
  key: K,
  value: T
) {
  return value[key]
}
